import { SearchRegistry } from '../types';

export const webhookManagementSearchEntries: SearchRegistry = {
  '/webhook-management/guide': {
    keywords: [
      'webhook guide',
      'webhook documentation',
      'webhook how-to',
      'webhook intro',
      'webhook setup guide',
      'event webhooks',
      'webhook overview',
      'central webhook management',
      'webhook centralized',
      'webhook model',
      'event types',
      'payload examples',
      'body template',
      'default subsets',
      'advanced setup',
      'default body templates',
      'editable body templates',
      'one template per outcome',
      'endpoint configuration',
      'webhook security',
      'authentication methods',
      'webhook payload',
      'rydpay webhooks',
      'fleet api webhooks',
      'ryd connect webhooks',
      'order webhooks',
      'invoice webhooks',
      'fleet status webhook',
      'integration lifecycle webhook',
      'no polling',
      'postman collection',
      'postman download',
      'webhook postman',
      'test webhook endpoint',
      'test webhooks locally',
    ],
    questions: [
      'How do webhooks work in ryd?',
      'Where is the webhook implementation guide?',
      'Which events can I subscribe to via webhooks?',
      'Where do I find webhook payload examples and body templates?',
      'Which webhook authentication methods are available?',
      'How do I create default webhook configurations?',
      'When should I use default subsets vs advanced setup?',
      'Is the webhook system shared across rydpay APIs, Fleet APIs and ryd Connect?',
      'Is there a Postman collection I can use to test my webhook endpoint?',
      'Where do I download the ryd webhook Postman collection?',
    ],
    faqs: [
      {
        q: 'How do I set up webhook delivery for my integration?',
        a: 'Read Webhook Management > Webhook Guide for the data model and security requirements, then open Webhook Configurations. The recommended flow is Use default subsets: enter your authentication and delivery details once, and ryd creates the standard success, failure, invoice and fleet configurations with editable default body templates. Use Advanced setup only when you need to build every topic, event filter, endpoint and body template yourself. Use Webhook Monitoring to verify delivery.',
      },
      {
        q: 'Is webhook configuration shared across rydpay, Fleet APIs and ryd Connect?',
        a: 'Yes — webhook management is centralised at ryd and applies across rydpay APIs, Fleet APIs, ryd Connect and other partner integration approaches. You configure endpoints once and subscribe to order, invoice, fleet-status and integration-lifecycle events without polling APIs continuously.',
      },
      {
        q: 'Is there a Postman collection I can use to test my webhook endpoint?',
        a: 'Yes. Open Webhook Management → Webhook Payloads and scroll to the Test against your endpoint section, or follow the Postman Collection card in the On this page list of the Webhook Guide. The download contains the typical order, invoice and fleet payloads and supports both API-key and OAuth 2.0 client-credentials authentication out of the box.',
      },
    ],
  },
  '/webhook-management/configurations': {
    keywords: [
      'webhook configurations',
      'webhook configs',
      'webhook setup',
      'configure webhooks',
      'webhook endpoint',
      'webhook url',
      'webhook subscriptions',
      'event subscriptions',
      'webhook authentication',
      'webhook auth method',
      'oauth webhook',
      'api key webhook',
      'body template',
      'default subsets',
      'use default subsets',
      'advanced setup',
      'manual setup',
      'default webhook bodies',
      'default webhook templates',
      'create 4 default configurations',
      'order success subset',
      'order failure subset',
      'invoice pdf ready subset',
      'fleet sponsor status subset',
      'webhook config list',
      'webhook config edit',
      'create webhook config',
      'event filter',
      'event type chips',
      'one configuration per outcome',
      'different event types different bodies',
      'different bodies per event type',
      'separate configurations per event type',
      'separate body templates per event type',
      'success and failure webhook bodies',
      'multiple webhook configurations',
      'accountIds error',
      'accountIds should not be empty',
      'default settings api key accountIds',
      'postman collection',
      'postman download',
      'webhook postman',
      'test webhook endpoint before activating',
      'dry run webhook',
      'simulate webhook locally',
    ],
    questions: [
      'How do I configure a webhook endpoint?',
      'Where do I add or edit my webhook subscriptions?',
      'Which webhook authentication methods can I configure myself?',
      'How do I subscribe to a subset of events?',
      'Where do I configure a webhook body template?',
      'How do I create different configurations for different event types?',
      'How do I use different body templates for different event types?',
      'Can success and failure events use different webhook bodies?',
      'Why do I get an accountIds validation error when creating webhook configurations?',
      'What does Use default subsets create?',
      'Can I edit the default webhook body templates after creation?',
      'When should I use Advanced setup for webhook configurations?',
      'How can I test my webhook endpoint before activating a configuration?',
      'Is there a Postman collection I can use to dry-run the typical payloads against my endpoint?',
    ],
    faqs: [
      {
        q: 'How do I create different configurations for different event types and bodies?',
        a: 'Create one webhook configuration for each event-type group that needs its own JSON body. In Webhook Configurations, use the recommended Use default subsets flow when the standard split fits your integration: ryd creates editable configurations for Order success, Order failure, Invoice PDF ready and Fleet sponsor status. Use Advanced setup when you need a custom split. In Advanced setup, add a configuration, choose the topic, select only the event types for that group, configure the endpoint and authentication details, then define the body template for that configuration. Repeat this for every event-type group that needs a different body.',
      },
      {
        q: 'Why do different event bodies need separate webhook configurations?',
        a: 'Each data-push endpoint configuration has one body template. If COMPLETED orders, FAILED orders, invoice PDFs or fleet sponsor status changes should be delivered with different JSON shapes, split them into separate configurations and scope each configuration with event-type filters. Events that can share the same body can stay together in one configuration.',
      },
      {
        q: 'Why do I get an accountIds validation error when creating webhook configurations?',
        a: 'You should not have to enter accountIds in the partner-facing portal. The new webhook configuration request uses the webhook-configurations API and scopes the configuration by your logged-in partnerId, which is your user group. When partnerId is set, accountIds are optional.',
      },
      {
        q: 'What does Use default subsets create in Webhook Configurations?',
        a: 'Use default subsets is the recommended creation flow. You enter your authentication method, credentials or API key, and data-push URL once. ryd then creates four editable webhook configurations: Order success (COMPLETED, COMPLETED_MANUALLY), Order failure (REJECTED, CANCELLED, PROBLEM, FAILED), Invoice PDF ready (INVOICE_GENERATED, RECEIPT_GENERATED), and Fleet sponsor status (ACTIVE, SUSPENDED, TERMINATED). Each generated configuration includes a default body template that you can edit after creation.',
      },
      {
        q: 'When should I use Advanced setup instead of Use default subsets?',
        a: 'Use Advanced setup when you need a custom webhook structure from the start, such as a different topic split, custom event-type filters, additional data-push endpoints, non-default body templates, or different endpoint details per configuration. In Advanced setup you manually add each topic configuration, event filter, endpoint and body template.',
      },
      {
        q: 'Can I edit the default webhook body templates?',
        a: 'Yes. The default subset flow creates body templates for the four standard outcome groups, but those templates remain editable. After creation, open each generated configuration and update the body template, event types, endpoint, authentication-related endpoint details, or active state as needed.',
      },
      {
        q: 'How can I test my webhook endpoint before activating a configuration?',
        a: 'Use the Postman collection link in the Webhook Configurations hero (Postman collection quick-link) or open Webhook Management → Webhook Payloads → Test against your endpoint. The collection POSTs the typical order, invoice and fleet payloads to a URL you control, supports both API-key and OAuth 2.0 client-credentials authentication, and lets you confirm the JSON shapes are accepted before you flip the configuration to Active.',
      },
    ],
  },
  '/webhook-management/event-types': {
    keywords: [
      'webhook event types',
      'webhook events',
      'event catalogue',
      'event catalog',
      'order events',
      'invoice events',
      'fleet status events',
      'eventType',
      'topic',
      'created completed failed cancelled',
      'fleet status webhook',
      'copy webhook event value',
      'default subsets',
      'order success events',
      'order failure events',
      'invoice pdf events',
      'fleet sponsor status events',
    ],
    questions: [
      'Which webhook event types does ryd support?',
      'Where do I find order, invoice and fleet status event values?',
      'Which event values should my endpoint handle?',
      'Which event types are included in the default subsets?',
    ],
    faqs: [
      {
        q: 'What webhook event types are supported?',
        a: 'ryd currently documents three webhook event categories: Order Events, Invoice Events and Fleet Status Events. Order events include CREATED, PAYMENT_AUTHORIZED, PRODUCT_ACQUIRED, COMPLETED, COMPLETED_MANUALLY, REJECTED, CANCELLED, PROBLEM, FAILED, PRODUCTS_AMENDED, ORDER_AMENDED, READY_FOR_INVOICE and INVOICE_GENERATION_REQUESTED. Invoice events include RECEIPT_GENERATED and INVOICE_GENERATED. Fleet status events include ACTIVE, PENDING, SUSPENDED and TERMINATED.',
      },
      {
        q: 'Which event types are included in the default webhook subsets?',
        a: 'The default subset setup creates four event filters: Order success uses COMPLETED and COMPLETED_MANUALLY; Order failure uses REJECTED, CANCELLED, PROBLEM and FAILED; Invoice PDF ready uses INVOICE_GENERATED and RECEIPT_GENERATED; Fleet sponsor status uses ACTIVE, SUSPENDED and TERMINATED.',
      },
    ],
  },
  '/webhook-management/payloads': {
    keywords: [
      'webhook payloads',
      'payload structure',
      'webhook json',
      'body template',
      'payload examples',
      'payload fields',
      'available fields',
      'order payload',
      'invoice payload',
      'fleet status payload',
      'build body template',
      'copy template',
      'dot path',
      'body.uuid',
      'default bodies',
      'default body templates',
      'one template per outcome',
      'different event types different bodies',
      'different bodies per event type',
      'separate configurations per event type',
      'separate body templates per event type',
      'order success body',
      'order failure body',
      'invoice pdf body',
      'fleet sponsor status body',
      'postman collection',
      'postman download',
      'webhook postman collection',
      'download postman collection',
      'ryd webhook postman',
      'postman api key',
      'postman oauth',
      'postman client credentials',
      'test webhook endpoint',
      'test webhook payloads',
      'dry run webhook payloads',
      'simulate webhook payloads',
      'webhook integration testing',
      'collection variables webhook',
      'webhookEndpointUrl variable',
      'apiKey variable',
      'accessToken variable',
      'oauthTokenUrl variable',
      'oauthClientId variable',
      'oauthClientSecret variable',
      'oauthScope variable',
      'fetch oauth access token',
      'json collection file',
    ],
    questions: [
      'Where do I see webhook payload examples?',
      'How do I build a webhook body template?',
      'Which fields can I include in a webhook payload?',
      'What does the partner endpoint receive for order, invoice and fleet status webhooks?',
      'How do I send different webhook bodies for success and failure events?',
      'How do I send different webhook bodies for different event types?',
      'Do I need separate configurations for different body templates?',
      'Which default body templates does ryd create?',
      'Where do I download the ryd webhook Postman collection?',
      'How do I test my webhook endpoint with Postman?',
      'How do I switch the Postman collection between API key and OAuth 2.0?',
      'How do I fetch an OAuth access token in the Postman collection?',
      'Which collection variables do I need to set in the Postman collection?',
      'Why does the downloaded Postman collection contain my partner name?',
    ],
    faqs: [
      {
        q: 'Where do I find the full webhook payload field list?',
        a: 'Open Webhook Management > Webhook Payloads. The page contains examples, an interactive body-template builder and the field catalogue for order, invoice and fleet status events.',
      },
      {
        q: 'How do I send different bodies for different webhook outcomes?',
        a: 'A data-push endpoint configuration sends one body template. To send different JSON shapes for different outcomes or event types, create separate configurations and scope each one by event-type filters. The default subset setup does this automatically for Order success, Order failure, Invoice PDF ready and Fleet sponsor status. You can edit each generated body template after creation. For a custom split, use Advanced setup and create one configuration per event-type group that needs a different body.',
      },
      {
        q: 'Where do I download the ryd webhook Postman collection?',
        a: 'Open Webhook Management → Webhook Payloads and scroll to the Test against your endpoint section (anchor #download). Click Download Postman collection to receive a JSON file you can import into Postman or run via newman. The same link is surfaced as a quick-link pill on Webhook Configurations and as a Postman Collection card in the Webhook Guide On this page list.',
      },
      {
        q: 'Which collection variables do I need to set in the Postman collection?',
        a: 'Set webhookEndpointUrl to the URL ryd should POST to. For API-key partners, set apiKey (and optionally apiKeyHeaderName, default X-API-Key) — the collection-level Authorization tab is preset to API Key, no further change required. For OAuth partners, set oauthTokenUrl, oauthClientId, oauthClientSecret and oauthScope, then run 000-setup-and-auth → 000-01-fetch-oauth-access-token once to populate the accessToken collection variable, and switch the collection Authorization from API Key to Bearer Token using {{accessToken}}.',
      },
      {
        q: 'How does the Postman collection support both API-key and OAuth authentication?',
        a: 'The collection ships with collection-level Authorization set to API Key (header name from {{apiKeyHeaderName}}, value from {{apiKey}}). A separate 000-setup-and-auth folder contains a client-credentials token request that stores the bearer token in the accessToken variable. To switch to OAuth, run that request once and change the collection-level Authorization tab from API Key to Bearer Token with value {{accessToken}}; every payload request inherits the new auth.',
      },
      {
        q: 'What is in the downloadable webhook Postman collection?',
        a: 'A v2.1.0 Postman collection named ryd-webhook-payloads (personalised with your partner id when downloaded while signed in). It contains a 000-setup-and-auth folder with an OAuth 2.0 client_credentials token request, plus four payload folders mirroring the page sections: 100-order-events-success (completed-rich, completed-minimal), 200-order-events-failure (failed, cancelled), 300-invoice-events (invoice-generated) and 400-fleet-events (fleet-active). Every request POSTs to {{webhookEndpointUrl}} and includes a basic 2xx status assertion. No example responses are saved.',
      },
    ],
  },
  '/webhook-management/authentication-methods': {
    keywords: [
      'webhook authentication methods',
      'webhook auth methods',
      'webhook security methods',
      'oauth client credentials webhook',
      'api key webhook',
      'hmac webhook',
      'bearer token webhook',
      'basic auth webhook',
      'asymmetric jwt webhook',
      'mtls webhook',
      'shared secret url webhook',
      'self-service webhook auth',
      'prepared by ryd',
      'default subset authentication',
      'advanced setup authentication',
      'postman collection',
      'postman api key',
      'postman oauth',
      'postman client credentials webhook',
      'test webhook authentication',
    ],
    questions: [
      'Which webhook authentication methods can I configure myself?',
      'Which webhook authentication methods need ryd to prepare them?',
      'How do OAuth and API key webhook authentication compare?',
      'When should I ask ryd to prepare HMAC or mTLS for webhooks?',
      'How can I test my webhook authentication setup with Postman?',
    ],
    faqs: [
      {
        q: 'Which webhook authentication methods can I configure myself?',
        a: 'You can configure OAuth 2.0 client credentials and API key authentication directly in webhook configurations. In the default subset flow, enter the shared authentication and delivery details once and ryd applies them to all four generated configurations. Other supported methods need ryd-side preparation on demand: HMAC signature in header, static bearer token, HTTP Basic Auth, asymmetric JWT, mutual TLS (mTLS) and shared secret in URL.',
      },
      {
        q: 'Which webhook authentication methods are supported?',
        a: 'Supported webhook authentication methods are OAuth 2.0 client credentials, API key in a custom header, HMAC signature in header, static bearer token, HTTP Basic Auth, asymmetric JWT, mutual TLS (mTLS) and shared secret in URL. The self-service options are OAuth 2.0 client credentials and API key; the others require ryd to prepare the setup.',
      },
      {
        q: 'How can I test my webhook authentication setup with Postman?',
        a: 'Download the Postman collection from Webhook Management → Webhook Payloads → Test against your endpoint. The collection ships with collection-level Authorization preset to API Key (using {{apiKey}} and {{apiKeyHeaderName}}). For OAuth 2.0 client credentials, fill oauthTokenUrl, oauthClientId, oauthClientSecret and oauthScope, run the 000-01-fetch-oauth-access-token request once to populate {{accessToken}}, then switch the collection Authorization tab from API Key to Bearer Token. Every payload request inherits whichever method is active, so you can verify your endpoint accepts the credentials before activating a webhook configuration.',
      },
    ],
  },
  '/webhook-management/monitoring': {
    keywords: [
      'webhook monitoring',
      'webhook delivery',
      'webhook errors',
      'webhook retries',
      'failed webhooks',
      'delivery health',
      'webhook log',
      'redelivery',
      'webhook log search',
      'partner environment switcher',
      'webhook delivery logs',
      'webhook attempts',
      'delivery attempt',
      'http status code',
      'response body',
    ],
    questions: [
      'Where do I see whether my webhooks are being delivered?',
      'How do I retry a failed webhook delivery?',
      'How do I troubleshoot webhook delivery errors?',
      'How do I switch between staging and production webhook logs?',
    ],
    faqs: [
      {
        q: 'A webhook delivery failed — how do I retry it?',
        a: 'Open Webhook Management → Webhook Monitoring, find the failed delivery and use the redeliver action. The system replays the original payload to your endpoint with a new delivery attempt.',
      },
      {
        q: 'How do I check whether a delivery succeeded against staging vs production?',
        a: 'Use the Partner Environment Switcher at the top of Webhook Monitoring. Your selection is persisted in `webhook-monitoring-environment` so the page remembers it; every search, export and shared link is scoped to the chosen environment.',
      },
    ],
  },
  '/webhook-management/circle-k-pcs-stations': {
    keywords: [
      'circle k',
      'circle k pcs',
      'pcs stations',
      'pcs station mapping',
      'dfs pcs',
      'dover pos',
      'prizma',
      'insertsale',
      'station identifier',
      'wash sales',
      'shadow mode',
      'live mode',
      'smartnow',
      'christ',
    ],
    questions: [
      'Where do I set up a Circle K station for PCS wash sales?',
      'How do I switch a Circle K PCS station from SHADOW to LIVE?',
      'How do I change the Prizma articles or payment types of a Circle K station?',
    ],
    faqs: [
      {
        q: 'How do I put a Circle K station live for PCS wash sales?',
        a: 'Open Webhook Management → Circle K PCS Stations (ryd internal, role bmw_circle_k_pcs), choose the environment and use Switch To LIVE on the station. Optionally send the sales stored while the station was in SHADOW. Every change is audited.',
      },
    ],
  },
  '/webhook-management/circle-k-pcs-monitoring': {
    keywords: [
      'circle k',
      'circle k pcs',
      'pcs monitoring',
      'pcs sales',
      'pcs registrations',
      'dfs pcs',
      'dover pos',
      'insertsale',
      'pos transaction id',
      'wash sales',
      'retry',
      'exception',
      'next try',
      'pcs calls',
      'stuck sales',
      'requeue',
    ],
    questions: [
      'Did sending a Circle K wash sale to PCS fail?',
      'How many tries did a Circle K PCS sale take and when is the next try?',
      'Where do I see the PCS request and response of an order?',
      'How do I requeue a Circle K PCS sale in EXCEPTION?',
    ],
    faqs: [
      {
        q: 'Where do I see whether a Circle K wash sale reached the POS?',
        a: 'Open Webhook Management → Circle K PCS → Monitoring (ryd internal, role bmw_circle_k_pcs); it shows the sales of this portal’s own webhook-gateway. Search by status, station, order or creation days; each sale shows status, attempts, next try, last error and POS transaction id, and Request Body / Response Body open what was sent to PCS and what it answered.',
      },
      {
        q: 'How do I send a failed or stuck Circle K PCS sale again?',
        a: 'Fix the station on Circle K PCS → Stations if needed, then use Requeue on the EXCEPTION or stuck sale in Circle K PCS → Monitoring. It builds the sale again with the current station mapping and queues it; every requeue is audited.',
      },
    ],
  },
  '/webhook-management/circle-k-pcs-how-it-works': {
    keywords: [
      'circle k',
      'circle k pcs',
      'how it works',
      'pcs flow',
      'insertsale',
      'prizma article',
      'payment type',
      'station setup',
      'sale status',
      'dover pos',
    ],
    questions: [
      'How does a ryd wash sale reach the Circle K POS?',
      'Why are Prizma articles and payment types entered by hand?',
      'Which values of a Circle K PCS sale come from the ryd order?',
      'What does each Circle K PCS sale status mean?',
    ],
    faqs: [
      {
        q: 'Why are the Circle K articles and payment types not taken from the order?',
        a: 'They are codes in Circle K’s POS (Dover Prizma) configuration: the article number is Circle K’s code for the wash and the PaymentType is the payment bucket Circle K books ryd sales under. The ryd order never contains them and PCS has no call to read them, so DFS and Circle K provide them per station and they are entered once on Circle K PCS → Stations. See Circle K PCS → How It Works.',
      },
    ],
  },
};
